import type { WidgetConfig } from './config';
import { trackEvents, type TrackingEventPayload, type TrackingQaClaim } from './api';
import { normalizeSwEventMeta } from './trackingMeta';
import { resolveSessionAcquisition } from './trackingAcquisition';
import { resolveSearchContext } from './trackingSearch';
import { interactionClass, interactionDedupeKey, type HumanInteractionClass } from './trackingHuman';
import { isCheckoutLoadedPath } from './trackingCheckout';
import { getSessionId } from './trackingSession';
import { flushTrackingQueue } from './trackingDelivery';
export { getSessionId } from './trackingSession';

const SCHEMA_VERSION = 1;
const MAX_BATCH = 20;
const FLUSH_MS = 1200;
const MAX_QUEUE_EVENTS = 100;
const MAX_BATCH_BYTES = 32 * 1024;
const DEBUG_TRACKING = false;
const TRACKING_CONTEXT_KEY = 'sw_tracking_context_v1';
const CHECKOUT_VIEW_STEPS: Record<string, string> = {
  'checkout-auth': 'login',
  'checkout-login': 'login',
  checkout: 'payment',
  'checkout-qnr': 'questionnaire',
  'checkout-confirm': 'confirm',
};

const SW_TO_CANONICAL: Record<string, string> = {
  'sw:add-to-cart': 'add_to_cart',
  'sw:remove-from-cart': 'remove_from_cart',
  'sw:login': 'login',
  'sw:signup': 'signup',
  'sw:logout': 'logout',
  'sw:checkout-start': 'checkout_start',
  'sw:checkout-click': 'checkout_click',
  'sw:checkout-auth-redirect': 'checkout_auth_redirect',
  'sw:checkout-validation-error': 'checkout_validation_error',
  'sw:checkout-submit': 'checkout_submit',
  'sw:checkout-attempt': 'checkout_attempt',
  'sw:checkout-payment-open': 'checkout_payment_open',
  'sw:checkout-exit': 'checkout_exit',
  'sw:order-placed': 'order_placed',
  'sw:view-change': 'view_change',
  'sw:search-results': 'search_results',
  'sw:search-result-click': 'search_result_click',
  'sw:newsletter-offer-impression': 'newsletter_offer_impression',
  'sw:newsletter-modal-open': 'newsletter_modal_open',
  'sw:newsletter-submit': 'newsletter_submit',
};

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}

function detectPageType(pathname: string): string {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/product')) return 'product';
  if (pathname.startsWith('/category')) return 'category';
  if (pathname.startsWith('/cart')) return 'cart';
  if (pathname.startsWith('/checkout')) return 'checkout';
  if (pathname === '/search' || pathname.startsWith('/search/')) return 'search';
  if (pathname.startsWith('/my-account')) return 'account';
  if (pathname.startsWith('/page') || pathname.startsWith('/help') || pathname.startsWith('/contact')) return 'static';
  return 'other';
}

function readAttr(name: string): string | null {
  const fromRoot = document.documentElement.getAttribute(name);
  if (fromRoot) return fromRoot;
  const fromBody = document.body?.getAttribute(name);
  if (fromBody) return fromBody;
  const fromAny = document.querySelector<HTMLElement>(`[${name}]`)?.getAttribute(name);
  return fromAny || null;
}

export function resolvePageContext(pathname: string): { pageType: string; categoryId?: string; productId?: string } {
  const explicitPageType = (readAttr('data-page-type') || '').trim();
  const pageType = explicitPageType || detectPageType(pathname);
  const categoryId = (readAttr('data-category-id') || '').trim() || undefined;
  const productId = (readAttr('data-product-id') || '').trim() || undefined;
  return { pageType, categoryId, productId };
}

function resolveTrackingContext(url: string): TrackingQaClaim | null {
  try {
    const parsed = new URL(url);
    if (parsed.searchParams.get('_tracking_context') === 'qa') {
      const expiresAt = Number.parseInt(parsed.searchParams.get('_tracking_qa_exp') ?? '0', 10);
      const signature = (parsed.searchParams.get('_tracking_qa_sig') ?? '').trim().toLowerCase();
      if (expiresAt > Math.floor(Date.now() / 1000) && /^[a-f0-9]{64}$/.test(signature)) {
        const claim = { expires_at: expiresAt, signature };
        sessionStorage.setItem(TRACKING_CONTEXT_KEY, JSON.stringify(claim));
        return claim;
      }
      sessionStorage.removeItem(TRACKING_CONTEXT_KEY);
      return null;
    }
    const claim = JSON.parse(sessionStorage.getItem(TRACKING_CONTEXT_KEY) ?? 'null') as Partial<TrackingQaClaim> | null;
    if (
      claim
      && Number.isInteger(claim.expires_at)
      && Number(claim.expires_at) > Math.floor(Date.now() / 1000)
      && /^[a-f0-9]{64}$/.test(String(claim.signature ?? ''))
    ) {
      return { expires_at: Number(claim.expires_at), signature: String(claim.signature) };
    }
    sessionStorage.removeItem(TRACKING_CONTEXT_KEY);
    return null;
  } catch {
    return null;
  }
}

function getDedupeStore(): Set<string> {
  const key = 'sw_event_dedupe_v1';
  const result = new Set<string>();
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return result;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      parsed.forEach((v) => {
        if (typeof v === 'string' && v !== '') result.add(v);
      });
    }
  } catch {
    // ignore
  }
  return result;
}

function saveDedupeStore(store: Set<string>): void {
  try {
    sessionStorage.setItem('sw_event_dedupe_v1', JSON.stringify(Array.from(store).slice(-500)));
  } catch {
    // ignore
  }
}

function canSendSingleEventPerPage(eventName: string): boolean {
  return eventName === 'page_view' || eventName === 'human_interaction';
}

function pickBatchByByteLimit(queue: TrackingEventPayload[]): TrackingEventPayload[] {
  const selected: TrackingEventPayload[] = [];
  let bytes = 0;
  while (queue.length > 0 && selected.length < MAX_BATCH) {
    const next = queue[0];
    const nextBytes = new Blob([JSON.stringify(next)]).size;
    if (selected.length > 0 && bytes + nextBytes > MAX_BATCH_BYTES) {
      break;
    }
    selected.push(queue.shift() as TrackingEventPayload);
    bytes += nextBytes;
    if (bytes >= MAX_BATCH_BYTES) {
      break;
    }
  }
  return selected;
}

export function initTrackingPipeline(config: WidgetConfig): void {
  if (typeof window === 'undefined' || window.location.pathname.includes('/preview-proxy')) return;

  const sessionId = getSessionId();
  const pagePath = window.location.pathname;
  const pageStartedAt = Date.now();
  const pageContext = resolvePageContext(pagePath);
  const searchContext = resolveSearchContext(window.location.href);
  const pageType = searchContext.pageType || pageContext.pageType;
  const acquisition = resolveSessionAcquisition(window.location.href, document.referrer);
  const trackingContext = resolveTrackingContext(window.location.href);
  const queue: TrackingEventPayload[] = [];
  const dedupeKeys = getDedupeStore();
  let flushTimer: number | null = null;
  const humanSent: Record<HumanInteractionClass, boolean> = { weak: false, strong: false };
  let checkoutActive = pageType === 'checkout' && isCheckoutLoadedPath(pagePath);
  let lastCheckoutStep = checkoutActive
    ? (pagePath === '/auth/signup/checkout' ? 'customer_data' : 'payment')
    : '';
  let lastSubmittedCheckoutStep = '';

  const enqueue = (event_name: string, meta?: Record<string, unknown>) => {
    if (canSendSingleEventPerPage(event_name)) {
      const dedupeKey = interactionDedupeKey(sessionId, pagePath, event_name, meta);
      if (dedupeKeys.has(dedupeKey)) {
        return;
      }
      dedupeKeys.add(dedupeKey);
      saveDedupeStore(dedupeKeys);
    }
    const mergedMeta = {
      ...(meta ?? {}),
      ...(config.locale ? { locale: config.locale } : {}),
      ...(trackingContext ? { tracking_context: 'qa' } : {}),
    };
    if (queue.length >= MAX_QUEUE_EVENTS) {
      queue.shift();
      if (DEBUG_TRACKING && typeof console !== 'undefined') {
        console.debug('[shop-widget][tracking] queue overflow, dropped oldest event');
      }
    }
    queue.push({
      event_name,
      schema_version: SCHEMA_VERSION,
      event_uuid: uuid(),
      event_ts: Date.now(),
      page_path: pagePath,
      page_type: pageType,
      session_id: sessionId,
      is_preview: false,
      meta: mergedMeta,
      ...(Object.keys(acquisition).length > 0 ? { acquisition } : {}),
    });
    if (queue.length >= MAX_BATCH) {
      void flush(false);
      return;
    }
    if (flushTimer == null) {
      flushTimer = window.setTimeout(() => {
        flushTimer = null;
        void flush(false);
      }, FLUSH_MS);
    }
  };

  const drainState = { busy: false, retryAt: 0 };
  const flush = async (useBeacon: boolean) => {
    await flushTrackingQueue(queue,
      batch => trackEvents(config, batch, useBeacon, trackingContext),
      pickBatchByByteLimit, drainState);
    if (queue.length && flushTimer == null) {
      flushTimer = window.setTimeout(() => {
        flushTimer = null;
        void flush(false);
      }, Math.max(FLUSH_MS, drainState.retryAt - Date.now()));
    }
  };

  enqueue('page_view', {
    referrer: document.referrer || undefined,
    category_id: pageContext.categoryId,
    product_id: pageContext.productId,
  });
  const newsletterOfferSurfaces = new Set<string>();
  document.querySelectorAll<HTMLElement>('[data-sl-newsletter-offer][data-newsletter-surface]').forEach((offer) => {
    const surface = (offer.getAttribute('data-newsletter-surface') || '').trim().slice(0, 64);
    if (surface === '' || newsletterOfferSurfaces.has(surface)) return;
    newsletterOfferSurfaces.add(surface);
    enqueue('newsletter_offer_impression', { surface });
  });
  if (pageType === 'checkout' && isCheckoutLoadedPath(pagePath)) {
    enqueue('checkout_loaded');
  }
  if (searchContext.query !== '') {
    enqueue('search', { search_query: searchContext.query });
    const resultRoot = document.querySelector<HTMLElement>('[data-sl-search-result-count]');
    const resultCount = Number(resultRoot?.getAttribute('data-sl-search-result-count'));
    if (Number.isFinite(resultCount) && resultCount >= 0) {
      enqueue('search_results', { search_query: searchContext.query, result_count: resultCount });
    }
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element
      ? event.target.closest<HTMLElement>('[data-sl-search-result-product-id]')
      : null;
    if (!target) return;
    enqueue('search_result_click', {
      search_query: searchContext.query,
      product_id: target.getAttribute('data-sl-search-result-product-id') || undefined,
      category_id: target.getAttribute('data-sl-search-result-category-id') || undefined,
      position: Number(target.getAttribute('data-sl-search-result-position')) || undefined,
    });
  }, { capture: true });

  const markHuman = (kind: string) => {
    const strength = interactionClass(kind);
    if (humanSent[strength]) return;
    humanSent[strength] = true;
    enqueue('human_interaction', {
      kind,
      engagement_ms: Math.max(0, Date.now() - pageStartedAt),
    });
  };
  window.addEventListener('click', () => markHuman('click'), { once: true, passive: true });
  window.addEventListener('keydown', () => markHuman('keydown'), { once: true, passive: true });
  window.addEventListener('touchstart', () => markHuman('touchstart'), { once: true, passive: true });
  window.addEventListener('scroll', () => markHuman('scroll'), { once: true, passive: true });
  window.addEventListener('pointermove', () => markHuman('pointermove'), { once: true, passive: true });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      markHuman('visibility_visible');
    }
  }, { once: true, passive: true });

  Object.keys(SW_TO_CANONICAL).forEach((ev) => {
    window.addEventListener(ev, (e: Event) => {
      const detail = (e as CustomEvent<Record<string, unknown>>).detail ?? {};
      const canonical = SW_TO_CANONICAL[ev];
      const meta = normalizeSwEventMeta(canonical, detail);
      if (canonical === 'checkout_submit' && typeof meta.step === 'string') {
        checkoutActive = true;
        lastCheckoutStep = meta.step;
        lastSubmittedCheckoutStep = meta.step;
      }
      if (canonical === 'checkout_validation_error' && typeof meta.step === 'string') {
        checkoutActive = true;
        lastCheckoutStep = meta.step;
      }
      if (canonical === 'view_change') {
        const view = String(detail.view ?? '');
        checkoutActive = Object.hasOwn(CHECKOUT_VIEW_STEPS, view);
        if (checkoutActive) lastCheckoutStep = CHECKOUT_VIEW_STEPS[view];
      }
      enqueue(canonical, meta);
    });
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush(true);
  });
  window.addEventListener('pagehide', () => {
    if (checkoutActive && lastCheckoutStep !== '') {
      enqueue('checkout_exit', {
        step: lastCheckoutStep,
        exit_reason: lastSubmittedCheckoutStep === lastCheckoutStep
          ? 'pagehide_after_submit'
          : 'pagehide_without_submit',
        engagement_ms: Math.max(0, Date.now() - pageStartedAt),
      });
    }
    void flush(true);
  });
}
